import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { defaultLocale } from '@/lib/i18n/config';
import LocaleLayout from './layouts/LocaleLayout';
import RequireAuth from './guards/RequireAuth';
import HomePage from './pages/HomePage';
import AreasPage from './pages/AreasPage';
import NewsListPage from './pages/NewsListPage';
import TeamPage from './pages/TeamPage';
import FaqsPage from './pages/FaqsPage';
import ContactPage from './pages/ContactPage';
import RequestPage from './pages/RequestPage';
import Placeholder from './pages/_Placeholder';

// Heavy detail pages with JSON-LD blocks split out so list pages stay light.
const AreaDetailPage = lazy(() => import('./pages/AreaDetailPage'));
const NewsDetailPage = lazy(() => import('./pages/NewsDetailPage'));

// Admin tree — most public visitors never load it.
const AdminLoginPage = lazy(() => import('./pages/admin/LoginPage'));
const AdminDashboardPage = lazy(() => import('./pages/admin/DashboardPage'));
const AdminServicesPage = lazy(() => import('./pages/admin/ServicesPage'));
const AdminTeamPage = lazy(() => import('./pages/admin/TeamPage'));
const AdminNewsPage = lazy(() => import('./pages/admin/NewsPage'));
const AdminFaqsPage = lazy(() => import('./pages/admin/FaqsPage'));
const AdminContactsPage = lazy(() => import('./pages/admin/ContactsPage'));
const AdminForensicRequestsPage = lazy(() => import('./pages/admin/ForensicRequestsPage'));
const AdminSettingsPage = lazy(() => import('./pages/admin/SettingsPage'));

function PageFallback() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center">
      <div
        className="h-8 w-8 animate-spin rounded-full border-2 border-accent border-t-transparent"
        aria-hidden="true"
      />
      <span className="sr-only">Cargando…</span>
    </div>
  );
}

function lazyRoute(node: React.ReactNode) {
  return <Suspense fallback={<PageFallback />}>{node}</Suspense>;
}

export default function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Navigate to={`/${defaultLocale}`} replace />} />

      <Route path="/:locale" element={<LocaleLayout />}>
        <Route index element={<HomePage />} />
        <Route path="areas" element={<AreasPage />} />
        <Route path="areas/:slug" element={lazyRoute(<AreaDetailPage />)} />
        <Route path="noticias" element={<NewsListPage />} />
        <Route path="noticias/:slug" element={lazyRoute(<NewsDetailPage />)} />
        <Route path="equipo" element={<TeamPage />} />
        <Route path="preguntas" element={<FaqsPage />} />
        <Route path="contacto" element={<ContactPage />} />
        <Route path="solicitar" element={<RequestPage />} />
      </Route>

      <Route path="/admin">
        <Route path="login" element={lazyRoute(<AdminLoginPage />)} />
        <Route element={<RequireAuth />}>
          <Route index element={lazyRoute(<AdminDashboardPage />)} />
          <Route path="servicios" element={lazyRoute(<AdminServicesPage />)} />
          <Route path="equipo" element={lazyRoute(<AdminTeamPage />)} />
          <Route path="noticias" element={lazyRoute(<AdminNewsPage />)} />
          <Route path="preguntas" element={lazyRoute(<AdminFaqsPage />)} />
          <Route path="contactos" element={lazyRoute(<AdminContactsPage />)} />
          <Route path="pericias" element={lazyRoute(<AdminForensicRequestsPage />)} />
          <Route path="ajustes" element={lazyRoute(<AdminSettingsPage />)} />
        </Route>
      </Route>

      <Route path="/offline" element={<Placeholder name="Offline" />} />
      <Route path="*" element={<Placeholder name="404 — No encontrado" />} />
    </Routes>
  );
}
